import React, { useEffect, useState, useCallback } from 'react'

// Accept any scrollable HTMLElement (ul, nav, etc.)
export function useOverflowNav(ref: React.RefObject<HTMLElement>) {
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const update = useCallback(() => {
    const el = ref.current
    if (!el) return
    const max = Math.max(0, el.scrollWidth - el.clientWidth)
    const x = el.scrollLeft
    const eps = 1
    setShowLeft(x > eps)
    setShowRight(x < max - eps)
  }, [ref])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // Run an initial update immediately and after layout settles
    update()
    const rafId = window.requestAnimationFrame(() => update())
    const timeoutId = window.setTimeout(() => update(), 0)
    // If fonts cause width changes, update when ready
    if ((document as any).fonts?.ready?.then) {
      ;(document as any).fonts.ready.then(() => update()).catch(() => {})
    }
    const onScroll = () => update()
    el.addEventListener('scroll', onScroll, { passive: true })
    const mo = new MutationObserver(update)
    mo.observe(el, { childList: true, subtree: true })
    // Observe size changes on the scroll container and its first child (usually the list)
    let ro: ResizeObserver | null = null
    let roParent: ResizeObserver | null = null
    let roRoot: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      try {
        ro = new ResizeObserver(() => update())
        ro.observe(el)
        if (el.firstElementChild && el.firstElementChild instanceof Element) ro.observe(el.firstElementChild)
        // Also observe parent containers so changes in layout that alter available width trigger updates
        if (el.parentElement) {
          roParent = new ResizeObserver(() => update())
          roParent.observe(el.parentElement)
          if (el.parentElement.parentElement) {
            roRoot = new ResizeObserver(() => update())
            roRoot.observe(el.parentElement.parentElement)
          }
        }
      } catch {
        // ignore
      }
    }
    return () => {
      el.removeEventListener('scroll', onScroll)
      mo.disconnect()
      ro?.disconnect?.()
      roParent?.disconnect?.()
      roRoot?.disconnect?.()
      window.cancelAnimationFrame(rafId)
      window.clearTimeout(timeoutId)
    }
  }, [update, ref])

  const scrollByPage = useCallback(
    (dir: number) => {
      const el = ref.current
      if (!el) return
      el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' })
    },
    [ref]
  )

  return { showLeft, showRight, scrollByPage, update }
}
